import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { api } from "@/lib/api";
import { goldenHourToday } from "@/lib/solar";
import { HERO_IMAGES } from "@/data/content";

const LINES = [
  { text: "Mehr als nur", italic: false },
  { text: "Bilder —", italic: true },
  { text: "es sind Erinnerungen.", italic: false },
];

export const Hero = () => {
  const ref = useRef(null);
  const [slide, setSlide] = useState(0);
  const [avail, setAvail] = useState(null);
  const [golden, setGolden] = useState(null);

  useEffect(() => {
    setGolden(goldenHourToday());
  }, []);

  useEffect(() => {
    const t = setInterval(() => setSlide((i) => (i + 1) % HERO_IMAGES.length), 8000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    api
      .get("/availability")
      .then((res) => {
        const m = res.data.months.find((x) => x.free > 0);
        if (m) setAvail(m);
      })
      .catch(() => {});
  }, []);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative flex min-h-screen items-end overflow-hidden">
      {/* Cinematische Vollbild-Slideshow mit Ken-Burns-Zoom */}
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <AnimatePresence mode="sync">
          <motion.img
            key={slide}
            src={HERO_IMAGES[slide]}
            alt="Leno Fotografie — Ausgewählte Arbeit"
            data-testid="hero-image"
            initial={{ opacity: 0, scale: 1.08 }}
            animate={{ opacity: 1, scale: 1.22 }}
            exit={{ opacity: 0 }}
            transition={{ opacity: { duration: 2 }, scale: { duration: 14, ease: "linear" } }}
            className="absolute inset-0 h-full w-full object-cover"
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/70 via-transparent to-transparent" />
      </motion.div>

      {/* Inhalt */}
      <motion.div
        style={{ y: contentY, opacity: fade }}
        className="relative z-10 mx-auto w-full max-w-[1440px] px-6 pb-24 pt-40 lg:px-12 lg:pb-28"
      >
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="mb-8 font-mono text-[11px] uppercase tracking-[0.35em] text-gold"
          data-testid="hero-eyebrow"
        >
          <span className="hidden sm:inline">Leno Fotografie · Momente | Natürlich | Echt</span>
          <span className="sm:hidden">Leno Fotografie · Ulm</span>
        </motion.p>

        <h1 className="font-serif text-5xl font-light leading-[1.05] tracking-tight text-white sm:text-7xl lg:text-[6rem]">
          {LINES.map((line, i) => (
            <span key={line.text} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${line.italic ? "italic text-zinc-300" : ""}`}
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.45 + i * 0.16, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8 }}
          className="mt-8 max-w-md text-sm leading-relaxed text-zinc-300 sm:text-base"
          data-testid="hero-subline"
        >
          Events, Hochzeiten, Unternehmen, Landschaften, Tiere & Automotive —
          ich halte fest, was zählt: natürliche, echte Momente, die bleiben.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-gallery-button"
            onClick={() => scrollToId("#portfolio")}
            className="group flex items-center gap-3 rounded-full bg-gold px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-ink transition-colors hover:bg-white"
          >
            Galerie entdecken
            <ArrowDown size={14} className="transition-transform group-hover:translate-y-0.5" />
          </button>
          <button
            data-testid="hero-cta-booking-button"
            onClick={() => scrollToId("#termine")}
            className="group flex items-center gap-3 rounded-full border border-white/30 px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-white backdrop-blur-sm transition-colors hover:border-gold hover:text-gold"
          >
            Termin anfragen
            <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="mt-12 flex flex-wrap items-center gap-x-10 gap-y-4"
        >
          {avail && (
            <button
              onClick={() => scrollToId("#termine")}
              data-testid="hero-availability-badge"
              className="group flex items-center gap-3 border border-gold/40 bg-ink/60 px-5 py-3 backdrop-blur-md transition-colors hover:border-gold"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gold opacity-60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-gold" />
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white">
                Noch <span className="text-gold">{avail.free} Termine</span> frei im {avail.label}
              </span>
            </button>
          )}
          {golden && (
            <span className="flex items-center gap-3 border border-white/10 bg-ink/60 px-5 py-3 backdrop-blur-md" data-testid="hero-golden-hour">
              <span className="h-2 w-2 rounded-full bg-terracotta" />
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-300">
                Golden Hour heute: <span className="text-gold">{golden.start} – {golden.end} Uhr</span>
              </span>
            </span>
          )}
          {[
            ["06", "Genres"],
            ["500+", "Momente festgehalten"],
            ["100%", "Natürlich & echt"],
          ].map(([num, label]) => (
            <div key={label}>
              <div className="font-serif text-2xl text-white sm:text-3xl">{num}</div>
              <div className="mt-1 font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-400">{label}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>

      {/* Slideshow-Punkte */}
      <div className="absolute bottom-8 right-6 z-10 flex gap-1.5 lg:right-12" data-testid="hero-slideshow-dots">
        {HERO_IMAGES.map((_, i) => (
          <button
            key={i}
            onClick={() => setSlide(i)}
            aria-label={`Bild ${i + 1}`}
            data-testid={`hero-slide-dot-${i}`}
            className={`h-1 rounded-full transition-all duration-500 ${i === slide ? "w-6 bg-gold" : "w-1.5 bg-white/40 hover:bg-white/70"}`}
          />
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="pointer-events-none absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 lg:block"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          className="flex flex-col items-center gap-2"
        >
          <p className="font-mono text-[9px] uppercase tracking-[0.35em] text-zinc-400">Scroll</p>
          <ArrowDown size={12} className="text-gold" />
        </motion.div>
      </motion.div>
    </section>
  );
};
